<template>
  <div class="wjs">
    <div class="wjs__head">
      <i class="bi bi-signpost-2" />
      {{ $t('components.wirepasAssets.detail.journey') }}
      <span class="wjs__count">{{ legs.length }}</span>
    </div>

    <div v-if="!legs.length" class="wjs__empty text-muted small">
      {{ $t('components.wirepasAssets.detail.journeyEmpty') }}
    </div>

    <div v-else class="wjs__list">
      <div
        v-for="(leg, i) in legs"
        :key="leg.gwId + leg.since"
        class="wjs__leg"
      >
        <!-- connector line -->
        <div v-if="i < legs.length - 1" class="wjs__line" />

        <!-- pin -->
        <div class="wjs__pin" :class="isCurrent(leg) ? 'wjs__pin--current' : ''" :style="{ background: isCurrent(leg) ? currentColor : typeColor(leg.locationType) }" />

        <!-- content -->
        <div class="wjs__content">
          <div class="wjs__loc-row">
            <span class="wjs__loc-name">{{ leg.locationName || leg.gwId }}</span>
            <span v-if="isCurrent(leg)" class="wjs__now">{{ $t('components.wirepasAssets.detail.journeyNow') }}</span>
            <span v-if="leg.locationType" class="wjs__type-badge" :class="`wjs__type-badge--${leg.locationType}`">{{ typeLabel(leg.locationType) }}</span>
          </div>
          <div class="wjs__times text-muted small font-monospace">
            <div>{{ $t('components.wirepasAssets.detail.journeySince') }}: {{ fmtTs(leg.since) }}</div>
            <div>{{ $t('components.wirepasAssets.detail.journeyUntil') }}: {{ fmtTs(leg.until ?? leg.lastSeen) }}</div>
          </div>
          <div class="wjs__dwell text-muted small">
            {{ $t('components.wirepasAssets.detail.journeyDwell') }}: {{ durText(leg.since, leg.until ?? Math.floor(Date.now() / 1000)) }}
            <template v-if="!leg.until"> ({{ $t('components.wirepasAssets.detail.journeyOngoing') }})</template>
          </div>

          <!-- transit gap to the next (older) leg -->
          <div v-if="transitFor(i)" class="wjs__transit text-muted small">
            <div class="wjs__transit-label">
              <i class="bi bi-truck" />
              {{ $t('components.wirepasAssets.detail.journeyTransit') }}: {{ durText(transitFor(i).from, transitFor(i).to) }}
            </div>
            <div class="font-monospace">{{ $t('components.wirepasAssets.detail.journeyTransitStart') }}: {{ fmtTs(transitFor(i).from) }}</div>
            <div class="font-monospace">{{ $t('components.wirepasAssets.detail.journeyTransitEnd') }}: {{ fmtTs(transitFor(i).to) }}</div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import { useI18n } from 'vue-i18n'
import { wirepasDurText, wirepasTypeColor, WIREPAS_CURRENT_COLOR } from '@/utils/wirepas'

const TYPE_LABEL: Record<string, Record<string, string>> = {
  pl: { hala: 'Hala', crossdock: 'Cross-dock', sklep: 'Sklep' },
  en: { hala: 'Hall', crossdock: 'Cross-dock', sklep: 'Store' },
}

export default defineComponent({
  name: 'WirepasJourneySidebar',
  props: {
    legs: { type: Array as () => any[], required: true },
  },
  setup(props) {
    const { locale } = useI18n()

    const isCurrent = (leg: any) => !leg.until
    const typeColor = wirepasTypeColor
    const typeLabel = (t: string) => TYPE_LABEL[locale.value]?.[t] ?? TYPE_LABEL['pl'][t] ?? t

    const fmtTs = (ts: number) => {
      const d = new Date(ts * 1000)
      return d.toLocaleString(locale.value, {
        day: '2-digit', month: '2-digit', year: 'numeric',
        hour: '2-digit', minute: '2-digit', second: '2-digit',
      })
    }

    const durText = (from: number, to: number) => wirepasDurText(from, to)

    // Gap between leaving the next (older) leg's location and arriving at this one — legs are
    // ordered newest-first, so "next" in the array is the previous stop in time.
    const transitFor = (i: number): { from: number; to: number } | null => {
      const prevLeg = props.legs[i + 1]
      if (!prevLeg || prevLeg.until == null) return null
      return { from: prevLeg.until, to: props.legs[i].since }
    }

    return { isCurrent, typeColor, typeLabel, fmtTs, durText, transitFor, currentColor: WIREPAS_CURRENT_COLOR }
  },
})
</script>

<style scoped lang="scss">
.wjs {
  display: flex;
  flex-direction: column;
  gap: 0;
}

.wjs__head {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--text-muted, #888);
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 14px;
}

.wjs__count {
  background: rgba(var(--primary-color-rgb), 0.12);
  color: var(--primary-color);
  border-radius: 999px;
  font-size: 10px;
  padding: 1px 7px;
  font-weight: 800;
}

.wjs__empty { padding: 12px 0; }

.wjs__list {
  display: flex;
  flex-direction: column;
  gap: 0;
}

.wjs__leg {
  display: flex;
  gap: 12px;
  position: relative;
  padding-bottom: 18px;
}

.wjs__line {
  position: absolute;
  left: 7px;
  top: 16px;
  width: 2px;
  bottom: 0;
  background: rgba(var(--primary-color-rgb), 0.2);
  border-left: 2px dashed rgba(var(--primary-color-rgb), 0.25);
}

.wjs__pin {
  width: 16px;
  height: 16px;
  border-radius: 50%;
  flex-shrink: 0;
  margin-top: 2px;
  border: 3px solid var(--card-bg, #fff);
  box-shadow: 0 1px 4px rgba(0,0,0,.3);
  position: relative;
  z-index: 1;

  &--current {
    width: 22px;
    height: 22px;
    border-color: #fff;
    margin-left: -3px;

    &::before {
      content: '';
      position: absolute;
      inset: 0;
      border-radius: 50%;
      background: inherit;
      animation: wjs-pulse 1.8s ease-out infinite;
    }
  }
}

@keyframes wjs-pulse {
  0%   { transform: scale(1); opacity: 0.55; }
  100% { transform: scale(2.2); opacity: 0; }
}

.wjs__content { flex: 1; min-width: 0; }

.wjs__loc-row {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
  margin-bottom: 3px;
}

.wjs__loc-name {
  font-weight: 700;
  font-size: 13px;
  color: var(--text-color, #252422);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.wjs__now {
  font-size: 10px;
  font-weight: 700;
  background: #CBEA9A;
  color: #2d5a1b;
  border-radius: 999px;
  padding: 1px 7px;
}

.wjs__type-badge {
  font-size: 10px;
  font-weight: 700;
  border-radius: 999px;
  padding: 1px 7px;

  &--hala      { background: rgba(72, 112, 184, 0.14); color: #4870b8; }
  &--crossdock { background: rgba(169, 128, 30, 0.14); color: #a9801e; }
  &--sklep     { background: rgba(63, 143, 82, 0.14); color: #3f8f52; }
}

.wjs__times { margin-bottom: 1px; }
.wjs__dwell { font-size: 11px; }

.wjs__transit {
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px dashed var(--border-color, rgba(0, 0, 0, 0.08));
  font-size: 11px;
  line-height: 1.5;
}

.wjs__transit-label {
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 5px;
  margin-bottom: 2px;
}
</style>
